import { useEffect, useRef, useState } from "react";

// We load Leaflet from CDN to avoid bundler config complexity.
// In a CRA app, you'd: npm install leaflet react-leaflet
// and use <MapContainer> from react-leaflet instead.

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
// Leaflet.markercluster: groups nearby live buses into a numbered cluster so
// the map never degenerates into an unreadable pile of overlapping badges.
const CLUSTER_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css";
const CLUSTER_DEFAULT_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
const CLUSTER_JS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";

let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (leafletReady) return cb();
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return; // already loading

  // CSS
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = LEAFLET_CSS;
  document.head.appendChild(link);
  const cl = document.createElement("link");
  cl.rel = "stylesheet";
  cl.href = CLUSTER_CSS;
  document.head.appendChild(cl);
  if (CLUSTER_DEFAULT_CSS) {
    const cld = document.createElement("link");
    cld.rel = "stylesheet";
    cld.href = CLUSTER_DEFAULT_CSS;
    document.head.appendChild(cld);
  }

  // JS
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = () => {
    // Leaflet loaded — now load markercluster (must come after Leaflet).
    const cs = document.createElement("script");
    cs.src = CLUSTER_JS;
    cs.onload = () => {
      leafletReady = true;
      leafletCallbacks.forEach((fn) => fn());
      leafletCallbacks = [];
    };
    document.head.appendChild(cs);
  };
  document.head.appendChild(script);
}

export default function BusMap({ stops = [], vehicles = [], userLocation, selectedStop, onStopClick, onVehicleClick, scrollWheelZoom = true }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const stopLayerRef = useRef(null);
  const vehicleLayerRef = useRef(null);
  const userLayerRef = useRef(null);
  const taxiLayerRef = useRef(null);
  const [taxi, setTaxi] = useState(null); // { ok, clearCount, eta, nearestKm }

  // Taxi ETA for the selected stop (if any) — nearest CLEAR driver to this stop.
  useEffect(() => {
    if (!selectedStop?.lat || !selectedStop?.lng) { setTaxi(null); return; }
    let cancelled = false;
    async function load() {
      try {
        const url = `/api/taxi-eta?lat=${encodeURIComponent(selectedStop.lat)}&lng=${encodeURIComponent(selectedStop.lng)}`;
        const resp = await fetch(url);
        if (!resp.ok) { setTaxi({ ok: true, clearCount: 0, eta: null, nearestKm: null }); return; }
        const data = await resp.json();
        if (!cancelled) setTaxi(data);
      } catch (_) { if (!cancelled) setTaxi({ ok: true, clearCount: 0, eta: null, nearestKm: null }); }
    }
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [selectedStop?.lat, selectedStop?.lng]);

  // Initialise map once Leaflet is loaded
  useEffect(() => {
    loadLeaflet(() => {
      if (mapRef.current || !containerRef.current) return;
      const L = window.L;

      const map = L.map(containerRef.current, {
        center: [50.3714, -4.1425],
        zoom: 13,
        zoomControl: true,
        scrollWheelZoom,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      // Stop markers: cluster group too. All Plymouth stops crowd at the
      // initial zoom, so grouping them into numbered bubbles keeps the main
      // map readable; they spread out as you zoom in.
      stopLayerRef.current = (L.markerClusterGroup ? L.markerClusterGroup({
        maxClusterRadius: 55,
        disableClusteringAtZoom: 16,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        iconCreateFunction: function (cluster) {
          const n = cluster.getChildCount();
          return L.divIcon({ html: n, className: "stop-cluster", iconSize: [46, 46] });
        },
      }) : L.layerGroup()).addTo(map);
      // Cluster group for live buses — auto-groups overlapping markers into a
      // numbered bubble; they split apart as you zoom in.
      vehicleLayerRef.current = (L.markerClusterGroup ? L.markerClusterGroup({
        maxClusterRadius: 46,
        disableClusteringAtZoom: 18,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
      }) : L.layerGroup()).addTo(map);
      userLayerRef.current = L.layerGroup().addTo(map);
      taxiLayerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
    });
  }, []);

  // Update stop markers
  useEffect(() => {
    if (!mapRef.current || !stopLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    stopLayerRef.current.clearLayers();

    stops.forEach((stop) => {
      const isSelected = selectedStop?.id === stop.id;
      // On-map marker: Alin's bus-stop pin (white teardrop map-pin with a black
      // bus silhouette + headlights). Non-selected = black pin, discreet.
      // Selected = larger red teardrop that pops out of the map.
      const w = isSelected ? 42 : 34;
      const h = Math.round((w * 320) / 240); // keep 240x320 aspect
      const pinFill = isSelected ? "#D32F2F" : "#000000";
      const icon = L.divIcon({
        className: "stop-marker-wrap",
        html: `
          <div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));">
            <svg width="${w}" height="${h}" viewBox="0 0 240 320" xmlns="http://www.w3.org/2000/svg">
              <!-- Map pin (black, red when selected) -->
              <path fill="${pinFill}" d="M120 8 C68 8 28 48 28 100 C28 152 88 230 120 312 C152 230 212 152 212 100 C212 48 172 8 120 8 Z"/>
              <!-- Inner white circle -->
              <circle cx="120" cy="100" r="68" fill="#ffffff"/>
              <!-- Bus body -->
              <rect x="78" y="62" width="84" height="62" rx="10" ry="10" fill="#000000"/>
              <!-- Windows -->
              <rect x="86" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
              <rect x="124" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
              <!-- Headlights -->
              <circle cx="96" cy="112" r="6" fill="#ffffff"/>
              <circle cx="144" cy="112" r="6" fill="#ffffff"/>
              <!-- Bumper / lower bar -->
              <rect x="86" y="122" width="68" height="6" rx="2" ry="2" fill="#000000"/>
              <!-- Wheels -->
              <rect x="90" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
              <rect x="134" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
            </svg>
          </div>
        `,
        iconSize: [w, h],
        iconAnchor: [w / 2, h], // bottom tip = the precise map point
      });

      const marker = L.marker([stop.lat, stop.lng], { icon })
        .bindPopup(`
          <strong style="font-size:13px;">${stop.name}</strong><br/>
          ${stop.distanceKm !== undefined ? `<span style="font-size:12px;color:#003087;font-weight:600;">${(stop.distanceKm * 1000).toFixed(0)}m away</span><br/>` : ""}
          <span style="font-size:11px;color:#555;">Routes: ${stop.routes.join(", ") || "—"}</span>
        `, { maxWidth: 220 })
        .on("click", () => onStopClick && onStopClick(stop));

      stopLayerRef.current.addLayer(marker);
    });
  }, [stops, selectedStop, onStopClick]);

  // Update live vehicle markers
  useEffect(() => {
    if (!mapRef.current || !vehicleLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    vehicleLayerRef.current.clearLayers();

    vehicles.forEach((v) => {
      // Live bus marker: a circular badge sized to stay readable WITHOUT
      // crowding when many buses cluster. Rotates to face travel direction.
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:34px; height:34px;
          background:#1254A6;
          border:2.5px solid #fff;
          border-radius:50%;
          display:flex; align-items:center; justify-content:center;
          color:#fff; font-size:13px; line-height:1; font-weight:800;
          box-shadow:0 2px 6px rgba(0,0,0,0.4);
          transform:rotate(${v.bearing || 0}deg);
        ">${v.route || "🚌"}</div>`,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });

      L.marker([v.lat, v.lng], { icon })
        .bindPopup(`
          <strong style="font-size:14px;">Route ${v.route}</strong><br/>
          <span style="font-size:12px;">${v.destination || ""}</span><br/>
          <span style="font-size:11px;color:#888;">${v.vehicleRef}</span>
        `)
        .on("click", () => onVehicleClick && onVehicleClick(v))
        .addTo(vehicleLayerRef.current);
    });
  }, [vehicles, onVehicleClick]);

  // Update user location marker
  useEffect(() => {
    if (!mapRef.current || !userLayerRef.current) return;
    const L = window.L;
    if (!L || !userLocation) return;
    userLayerRef.current.clearLayers();

    const icon = L.divIcon({
      className: "",
      // A proper "you are here" location pin — blue dot with white ring and
      // a short direction nib, Google-Maps style, clearly visible at any zoom.
      html: `<div style="position:relative;width:42px;height:42px;transform:translate(-50%,-100%);">
        <!-- soft outer halo -->
        <div style="position:absolute;inset:0;border-radius:50%;background:rgba(0,116,217,0.15);animation:lbl-pulse 2s infinite;"></div>
        <!-- white ring -->
        <div style="position:absolute;left:50%;top:50%;width:22px;height:22px;transform:translate(-50%,-50%);border-radius:50%;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,0.35);"></div>
        <!-- blue solid dot -->
        <div style="position:absolute;left:50%;top:50%;width:11px;height:11px;transform:translate(-50%,-50%);border-radius:50%;background:#0074D9;box-shadow:0 0 3px rgba(0,116,217,0.9);"></div>
      </div>`,
      iconSize: [42, 42],
      iconAnchor: [21, 42],
    });

    L.marker([userLocation.lat, userLocation.lng], { icon })
      .bindPopup("You are here")
      .addTo(userLayerRef.current);

    // Find the nearest bus stop to the user, so we can zoom exactly onto it.
    // "Enable location" should put the stop right beside the user in focus.
    let target = null;
    let nearestDist = Infinity;
    if (Array.isArray(stops) && stops.length > 0) {
      for (const s of stops) {
        const d = typeof s.distanceKm === "number" ? s.distanceKm : Infinity;
        if (d < nearestDist) {
          nearestDist = d;
          target = s;
        }
      }
    }

    if (target) {
      // Zoom onto the nearest stop: centre the map there, tight enough to
      // make the stop pin the clear focal point.
      mapRef.current.flyTo([target.lat, target.lng], 17, { duration: 1.0 });
    } else {
      // No stops nearby yet — fall back to framing 400m around the user.
      mapRef.current.flyTo([userLocation.lat, userLocation.lng], 15, { duration: 1.0 });
    }
  }, [userLocation, stops]);

  // Taxi 🚕 marker: nearest clear taxi relative to the selected stop, when
  // available. Drawn as a small offset badge so it sits beside the stop.
  useEffect(() => {
    if (!mapRef.current || !taxiLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    taxiLayerRef.current.clearLayers();
    if (!taxi?.ok || !taxi.clearCount || !selectedStop?.lat || !selectedStop?.lng) return;
    const taxiStop = { lat: selectedStop.lat + 0.0015, lng: selectedStop.lng + 0.0015 };
    L.marker([taxiStop.lat, taxiStop.lng], {
      icon: L.divIcon({
        className: "",
        html: `<div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
          <svg width="60" height="34" viewBox="0 0 700 400" xmlns="http://www.w3.org/2000/svg" style="overflow:visible;">
            <!-- rear wheel (dark grey rim on black body) -->
            <circle cx="190" cy="292" r="40" fill="#111"/>
            <circle cx="190" cy="292" r="26" fill="#2b2b2b"/>
            <circle cx="190" cy="292" r="6" fill="#555"/>
            <!-- front wheel -->
            <circle cx="490" cy="300" r="44" fill="#111"/>
            <circle cx="490" cy="300" r="28" fill="#2b2b2b"/>
            <circle cx="490" cy="300" r="7" fill="#555"/>
            <!-- body (black) -->
            <path fill="#1a1a1a" stroke="#4a4a4a" stroke-width="2" d="M130 210 C135 160 170 125 230 112 C280 100 340 96 400 102 C470 110 530 132 575 168 C610 196 635 230 648 258 C656 276 650 292 630 300 C600 310 540 308 490 304 C430 300 360 298 280 292 C210 286 150 270 136 240 C130 228 128 218 130 210Z"/>
            <!-- lower shadow strip -->
            <path fill="#2b2b2b" d="M140 250 C160 272 230 288 320 294 C410 300 500 304 580 296 C620 292 640 282 642 268 C620 278 540 286 430 286 C320 286 210 274 150 258 C142 254 140 252 140 250Z"/>
            <!-- windshield -->
            <path fill="#0e0e0f" stroke="#3a3a3c" stroke-width="1.5" d="M392 112 C448 118 508 140 548 168 C528 186 498 200 460 206 C422 212 388 206 362 192 C370 162 380 132 392 112Z"/>
            <!-- rear window -->
            <path fill="#0e0e0f" d="M228 120 C262 108 300 104 338 108 C330 130 320 150 306 164 C274 156 246 146 226 138 C224 132 224 126 228 120Z"/>
            <!-- mid window -->
            <path fill="#0e0e0f" d="M348 112 C360 140 366 162 368 184 C348 178 326 172 306 166 C316 148 330 128 348 112Z"/>
            <!-- pillars -->
            <path d="M338 108 L306 166" stroke="#1a1a1a" stroke-width="7" fill="none"/>
            <path d="M392 112 L368 184" stroke="#1a1a1a" stroke-width="8" fill="none"/>
            <!-- door lines -->
            <path d="M306 166 C300 200 300 236 308 276" stroke="#3a3a3a" stroke-width="2" fill="none"/>
            <path d="M368 184 C366 220 368 252 376 286" stroke="#3a3a3a" stroke-width="2" fill="none"/>
            <!-- handles -->
            <rect x="322" y="214" width="18" height="5" rx="2" fill="#4a4a4a"/>
            <rect x="402" y="218" width="18" height="5" rx="2" fill="#4a4a4a"/>
            <!-- mirror -->
            <ellipse cx="532" cy="186" rx="22" ry="14" fill="#2b2b2b"/>
            <ellipse cx="536" cy="186" rx="10" ry="7" fill="#111"/>
            <!-- front light bar (keep bright so front is visible) -->
            <path fill="#cfd8dc" stroke="#111" stroke-width="3" d="M575 198 C604 214 624 230 636 246 C620 248 598 236 576 220 C572 210 572 202 575 198Z"/>
            <!-- rear lamp (red) -->
            <path fill="#8a1a1a" d="M142 188 C156 178 170 184 172 198 C160 208 144 204 142 188Z"/>
            <!-- taxi sign (black sign, amber text) -->
            <rect x="352" y="58" width="78" height="40" rx="8" fill="#000"/>
            <rect x="360" y="66" width="62" height="24" rx="4" fill="#e7d9c4"/>
          </svg>
        </div>`,
        iconSize: [60, 34],
        iconAnchor: [30, 17],
      }),
    })
      .bindPopup(`<strong>🚕 Taxi Services</strong><br/><small>${taxi.clearCount} drivers clear · ~${taxi.eta} min</small>`)
      .addTo(taxiLayerRef.current);
  }, [taxi, selectedStop]);

  // Pan to selected stop
  useEffect(() => {
    if (!mapRef.current || !selectedStop) return;
    mapRef.current.flyTo([selectedStop.lat, selectedStop.lng], 16, { duration: 0.8 });
  }, [selectedStop]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "260px",
        borderRadius: "12px",
        overflow: "hidden",
        border: "0.5px solid var(--color-border-tertiary)",
        marginBottom: "0.75rem",
      }}
    />
  );
}
